import { Component, DestroyRef, inject, ChangeDetectionStrategy, ElementRef, viewChild } from '@angular/core';
import { ThemeService } from './theme.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '../../environments/environment';
import { OrderFlowAuthService } from '../auth/order-flow-auth.service';
import { AvatarCardComponent } from '../shared/avatar-card.component';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { UsersService } from '../shared/access/users.service';
import { switchMap, filter } from 'rxjs/operators';
import { OrdersNavMenuComponent } from '../orders/orders-nav-menu.component';
import { PreTradeCheckNavMenuComponent } from '../compliance/pre-trade-check/pre-trade-check-nav-menu.component';
import { RefreshCacheMenuComponent } from './refresh-cache-menu.component';
import { NotificationDrawerComponent } from './notification-drawer.component';
import { ReportsNavMenuComponent } from './reports-nav-menu.component';
import { RoleGateComponent } from '../shared/authz/role-gate.component';
import { ROLE_GROUPS } from '../shared/authz/role-groups';

const ENV_BADGE_LABELS: Record<string, string> = {
  development: 'DEV',
  staging: 'STAGING',
  uat: 'UAT'
};

@Component({
  selector: 'of-nav-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AvatarCardComponent,
    RouterModule,
    OrdersNavMenuComponent,
    PreTradeCheckNavMenuComponent,
    RoleGateComponent,
    RefreshCacheMenuComponent,
    NotificationDrawerComponent,
    ReportsNavMenuComponent
  ],
  template: `
    @if (loggedIn()) {
      <nav class="navbar sticky-top navbar-expand-lg py-0 of-tbar">
        <div class="container-fluid of-tbar__inner">
          <div class="d-flex align-items-center gap-2">
            <button
              class="navbar-toggler d-flex d-lg-none of-tbar__toggler"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvasNavbar"
              aria-controls="offcanvasNavbar"
              aria-label="Toggle navigation"
            >
              <i class="bi bi-list of-tbar__toggler-icon"></i>
            </button>
            <a class="navbar-brand of-tbar__brand" [routerLink]="['/']">
              <img src="assets/cigp-logo.png" class="of-tbar__logo" alt="CIGP" />
              <span class="of-tbar__brand-text">Order Flow</span>
              @if (envBadge) {
                <sup class="of-tbar__env" [title]="'Environment: ' + environment">{{ envBadge }}</sup>
              }
            </a>
          </div>

          <ul class="navbar-nav d-flex flex-row align-items-center gap-1 of-tbar__actions">
            <of-refresh-cache-menu></of-refresh-cache-menu>
            <of-notification-drawer></of-notification-drawer>
            <li class="nav-item">
              <button
                class="of-tbar__icon-btn"
                (click)="themeService.toggle()"
                [title]="themeService.isDark() ? 'Switch to light mode' : 'Switch to dark mode'"
              >
                @if (themeService.isDark()) {
                  <i class="bi bi-sun"></i>
                } @else {
                  <i class="bi bi-moon-stars"></i>
                }
              </button>
            </li>
            <li class="nav-item dropdown">
              <of-avatar-card
                class="nav-link of-tbar__avatar-btn"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                id="userMenuDropdown"
                [user]="me()"
                [styles]="{
                  '--of-avatar-size': '30px',
                  '--of-avatar-initials-size': '11px',
                  'border-radius': '50%',
                  overflow: 'hidden'
                }"
              ></of-avatar-card>
              <ul class="dropdown-menu dropdown-menu-end of-tbar__dropdown" aria-labelledby="userMenuDropdown">
                <li>
                  <button type="button" class="dropdown-item" (click)="logout()">
                    <i class="bi bi-box-arrow-right"></i> Logout
                  </button>
                </li>
                @if (userManual) {
                  <li>
                    <a class="dropdown-item" [href]="userManual" target="_blank"
                      ><i class="bi bi-question-circle"></i> User Manual</a
                    >
                  </li>
                }
                @if (feedbackForm) {
                  <li>
                    <a class="dropdown-item" [href]="feedbackForm" target="_blank"
                      ><i class="bi bi-chat-dots"></i> Feedback</a
                    >
                  </li>
                }
              </ul>
            </li>
          </ul>
        </div>
      </nav>

      <div
        #offcanvasElement
        class="offcanvas offcanvas-start of-oc"
        tabindex="-1"
        id="offcanvasNavbar"
        aria-labelledby="offcanvasNavbarTitle"
        data-bs-backdrop="true"
      >
        <div class="offcanvas-header of-oc__header">
          <div class="of-oc__brand">
            <img src="assets/cigp-logo.png" class="of-oc__logo" alt="CIGP" />
            <span class="of-oc__title" id="offcanvasNavbarTitle">Order Flow</span>
            @if (envBadge) {
              <sup class="of-tbar__env" [title]="'Environment: ' + environment">{{ envBadge }}</sup>
            }
          </div>
          <button type="button" class="of-oc__close" (click)="closeOffcanvas()" aria-label="Close navigation">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div class="offcanvas-body of-oc__body">
          <p class="of-oc__section-label">Orders</p>
          <of-orders-nav-menu></of-orders-nav-menu>
          <p class="of-oc__section-label mt-3">Counterparty</p>
          <of-role-gate [includeRoles]="roleGroups.brokerView">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/brokers']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
              >
                <i class="bi bi-building me-2"></i>
                <span>Brokers</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.custodianView">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/custodians']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
              >
                <i class="bi bi-bank me-2"></i>
                <span>Custodians</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.fiduciaryView">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/fiduciaries']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
              >
                <i class="bi bi-person-vcard me-2"></i>
                <span>Fiduciaries</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.tdRatesView">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/td-rates']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
              >
                <i class="bi bi-percent me-2"></i>
                <span>TD Rates</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.feeView">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/fees']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
              >
                <i class="bi bi-cash-coin me-2"></i>
                <span>Fee Schedule</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.fundamentalData">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/securities']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
              >
                <i class="bi bi-graph-up me-2"></i>
                <span>Securities</span>
              </a>
            </div>
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/portfolios']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
              >
                <i class="bi bi-briefcase me-2"></i>
                <span>Portfolios</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.orderReadWrite"
            ><of-pre-trade-check-nav-menu></of-pre-trade-check-nav-menu
          ></of-role-gate>
          <of-reports-nav-menu></of-reports-nav-menu>
        </div>
      </div>
    }
  `
})
export class NavMenuComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly orderFlowAuthService = inject(OrderFlowAuthService);
  private readonly usersService = inject(UsersService);
  protected readonly roleGroups = ROLE_GROUPS;
  protected readonly feedbackForm = environment.feedbackForm;
  protected readonly userManual = environment.userManual;
  protected readonly environment = environment.env;
  // Superscript badge next to the brand; null in production so nothing renders there.
  protected readonly envBadge =
    environment.env === 'production' ? null : (ENV_BADGE_LABELS[environment.env] ?? environment.env.toUpperCase());
  protected readonly loggedIn = toSignal(this.orderFlowAuthService.loggedIn$, { initialValue: false });
  readonly themeService = inject(ThemeService);
  protected readonly me = toSignal(
    this.orderFlowAuthService.loggedIn$.pipe(
      filter((loggedIn) => loggedIn && !!this.orderFlowAuthService.account),
      switchMap(() => this.usersService.getUser$(this.orderFlowAuthService.account!.username)),
      takeUntilDestroyed(this.destroyRef)
    ),
    { initialValue: null }
  );
  protected readonly offcanvasElement = viewChild<ElementRef<HTMLDivElement>>('offcanvasElement');
  constructor() {
    inject(Router)
      .events.pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.closeOffcanvas());
  }

  protected logout(): void {
    this.orderFlowAuthService.logout$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  protected closeOffcanvas(): void {
    const element = this.offcanvasElement()?.nativeElement;
    if (element) {
      const bsOffcanvas = window.bootstrap?.Offcanvas.getInstance(element);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  }
}
